import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as GreenCircleIcon } from '../../../../assets/svg/green-circle-icon.svg';
import Button from '../../../../generic-components/Button';
import { convertValueToUSD } from '../../../../helpers/currency.helper';
import { ChooseCategory } from '../ChooseCategory';
import { useStore } from '../../../../store';
import { ICategory, ITransaction } from '../../../../types';
import { SplitTransactionRangeSlider } from '../splitTransactionRangeSlider';
import { formatDate } from '../../helpers';
import { getTransactionPayeeName } from '../../../../helpers/transaction.helper';
import { getIconFromTrxType } from '../../../../generic-components/LatestTransactions/constants';
import Icon from '../../../../generic-components/Icon';

const STYLES = {
  HEADER: 'flex h-[58px] box-content items-center border-b-[1px] border-opacity-50 border-bw05 pl-[14px] pr-[11px] pt-[14px] justify-between',
  CLOSE_ICON: 'rounded-full fill-bw07 hover:bg-bw02 cursor-pointer relative top-[-1px]',
  TITLE: 'ml-[10px] text-bw08 font-metricRMedium text-md leading-6',
  DATE: 'text-bw06 text-[14px] font-metricR leading-6',
  CONTENT: 'h-[413px] pt-[32px] px-[20px] pb-[56px]',
  FOOTER: 'h-[74px] shadow-splitComponent flex justify-center items-center',
  BUTTON: 'flex w-[319px] h-[42px] justify-center items-center rounded bg-action03 hover:bg-action04 text-[#fff] text-sm leading-5 font-metricRMedium',
  DETAILS: 'px-[20px] flex items-center h-[32px] justify-between',
  MERCHANT: 'flex items-center',
  MERCHANT_NAME: 'text-bw08 font-metricRMedium text-md leading-6 truncate w-[136px]',
  AMOUNT: 'flex text-bw08 font-metricRMedium text-md leading-6 justify-end',
  CHOOSE_CATEGORY: 'mx-[20px] pt-[11px] pb-[19px] border-b-[0.5px] border-bw05 border-opacity-50',
};

interface ISplitTransaction {
  onChooseCategoryClick: () => void;
  transaction: ITransaction;
  selectedCategoryId: string;
  handleSubmit: (catId: string | null, businessPercent: number | null) => void;
  headerIcon: React.ReactNode;

}

const DEFAULT_BUSINESS_PERCENT = 0.5;
export const SplitTransaction = (
  {
    onChooseCategoryClick,
    transaction,
    selectedCategoryId,
    handleSubmit,
    headerIcon,
  }: ISplitTransaction,
) => {
  const chosenBusinessPercent = transaction?.categoryDetails?.businessPercent;
  const [businessPercent, setBusinessPercent] = useState<number>((chosenBusinessPercent || chosenBusinessPercent === 0) ? chosenBusinessPercent : DEFAULT_BUSINESS_PERCENT);
  const splitTransactionBusinessPercent = useStore(state => state.splitTransactionBusinessPercent);
  const setSplitTransactionBusinessPercent = useStore(state => state.setSplitTransactionBusinessPercent);
  const transactionsCategories = useStore(state => state.categoriesList);
  const filteredCategoriesByType = useMemo(() => transactionsCategories.categories.filter((c: ICategory) => c.transactionType === transaction.categorizationType), [transactionsCategories, transaction.categorizationType]);
  const latestBusinessPercent = splitTransactionBusinessPercent || splitTransactionBusinessPercent === 0 ? splitTransactionBusinessPercent : businessPercent;
  const { t } = useTranslation();
  const onMainCTAButtonClick = () => {
    if (selectedCategoryId) {
      handleSubmit(selectedCategoryId, latestBusinessPercent);
      setSplitTransactionBusinessPercent(null);
    } else {
      setSplitTransactionBusinessPercent(businessPercent);
      handleSubmit(null, null);
    }
  };
  const handleOnChooseCategoryClick = () => {
    if (businessPercent !== DEFAULT_BUSINESS_PERCENT) setSplitTransactionBusinessPercent(businessPercent);
    if (onChooseCategoryClick) onChooseCategoryClick();
  };
  const transactionIcon = getIconFromTrxType(transaction);

  return (
    <div>
      <div className={STYLES.HEADER}>
        <div className='flex items-center'>
          {headerIcon}
          <div className={STYLES.TITLE}>{t('TRANSACTION_SPLIT.TITLE')}</div>
        </div>
        <div className={STYLES.DATE}>{formatDate(transaction.timestamp)}</div>
      </div>
      <div className={STYLES.CONTENT}>
        <div className={STYLES.DETAILS}>
          <div className={STYLES.MERCHANT}>
            {transactionIcon !== '' ? <Icon iconId={`transaction_${transaction.transactionId}_icon`} className='w-[2rem] min-w-[2rem] mr-[10px]' src={transactionIcon} /> : <GreenCircleIcon className='mr-[10px]' /> }
            <div className={STYLES.MERCHANT_NAME}>{getTransactionPayeeName(transaction)}</div>
          </div>
          <div className={STYLES.AMOUNT} data-testid='categorization-split-transaction-amount'>{convertValueToUSD(Math.abs(transaction.amount), 2)}</div>
        </div>
        <div className={STYLES.CHOOSE_CATEGORY}>
          <ChooseCategory transactionsCategories={filteredCategoriesByType} transaction={transaction} onCategorySelection={handleOnChooseCategoryClick} selectedCategoryId={selectedCategoryId} />
        </div>
        <SplitTransactionRangeSlider
          businessPercent={latestBusinessPercent}
          setBusinessPercent={setBusinessPercent}
          shouldDisplaySplittingResult
          transactionAmount={Math.abs(transaction.amount)}
        />
      </div>
      <div className={STYLES.FOOTER}>
        <Button title='Split Transaction' className={STYLES.BUTTON} onClick={onMainCTAButtonClick} id='splitTransactionCTA' />
      </div>
    </div>
  );
};
